import {
  createContext,
  type PropsWithChildren,
  useContext,
  useEffect,
  useRef,
} from 'react'
import { createStore, useStore } from 'zustand'
import { useShallow } from 'zustand/shallow'

import type { AppointmentStatus, TrackingStatus } from '@fv/models'

import type {
  OppListingState,
  OppListingStatus,
  OppListingStore,
  OppPage,
} from './types'

type OppListingStoreEntity = ReturnType<typeof createOppListingStore>
const createOppListingStore = (state?: Partial<OppListingState>) => {
  const defaultStore: OppListingState = {
    status: 'open',
    page: 'open',
    activeId: '',
    oppPanelTab: 'messages',
    isOppPanelOpen: false,
    ...state,
  }
  return createStore<OppListingStore>()(set => ({
    ...defaultStore,
    actions: {
      setOppPanelTab: oppPanelTab => set({ oppPanelTab }),
      setActiveId: activeId => set({ activeId }),
      setOppPanelOpen: isOppPanelOpen => set({ isOppPanelOpen }),
    },
  }))
}

const OppListingContext = createContext<OppListingStoreEntity | null>(null)

type Props = PropsWithChildren<{
  status?: OppListingStatus
  page: OppPage
  trackingStatus?: TrackingStatus
  appointmentStatus?: AppointmentStatus
}>
export const OppListingProvider = ({
  children,
  status,
  page,
  trackingStatus,
  appointmentStatus,
}: Props) => {
  const store = useRef(
    createOppListingStore({ status, page, trackingStatus, appointmentStatus }),
  ).current
  useEffect(() => {
    store.setState({
      status,
      page,
      trackingStatus,
      appointmentStatus,
    })
  }, [store, status, page, trackingStatus, appointmentStatus])

  return (
    <OppListingContext.Provider value={store}>
      {children}
    </OppListingContext.Provider>
  )
}

export function useOppListingContext<T>(
  selector: (state: OppListingStore) => T,
): T {
  const store = useContext(OppListingContext)
  if (!store) throw new Error('Missing OppListingProvider in the tree')
  return useStore(store, selector)
}

export const useOppListingActions = () => {
  const store = useContext(OppListingContext)
  if (!store) throw new Error('Missing OppListingProvider in the tree')
  return useStore(store, s => s.actions)
}

export const useOppListingState = () => {
  return useOppListingContext(useShallow(({ actions, ...state }) => state))
}
